import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { identity, merge, pullAt, random, times } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { ShapeOrGroupOrderItem } from '../../../cross-team-shared/state/shape-group-state';
import { PointCloudService } from '../../render/mesh/cloud/point-cloud.service';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { CuboidState3D } from '../../state/cuboid-state-3d';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { ObjectWith } from '../../utils/misc-utils';

@Component({
  selector: 'h3-add-random-cuboids',
  templateUrl: './add-random-cuboids.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddRandomCuboidsComponent extends DirectiveSuperclass {
  numShapes = 1;
  percentageOfKeyLocationsToAdd = 10;
  useSingleFrame = true;
  isVisible = true;

  private pointCloudService = inject(PointCloudService);
  private store = inject(PointCloudContentStore);

  add() {
    const shapes: ObjectWith<CuboidState3D> = {};
    const orderItems: ShapeOrGroupOrderItem[] = [];
    const nextInternalId = this.store.state().task.next_internal_id;

    for (let i = 0; i < this.numShapes; ++i) {
      const index = nextInternalId + i;
      shapes[index] = {
        type: 'cuboid',
        index,
        tags: {},
        dynamic_tags: {},
        key_locations: this.pickFrameNumbers().map((frameNumber) =>
          this.buildKeyLocation(frameNumber),
        ),
      };
      orderItems.push({ type: 'shape', index });
    }

    this.store('task')('next_internal_id').set(nextInternalId + this.numShapes);

    this.store.batch(() => {
      this.store('render')('cuboids').mutateUsing(merge, shapes);
      this.store('shapeAndGroupOrder').setUsing((currentOrder) => [
        ...currentOrder,
        ...orderItems,
      ]);
    });
  }

  buildKeyLocation(frameNumber: number) {
    const currentCloud = this.pointCloudService.getCurrentCloud();
    currentCloud.cloud.geometry.computeBoundingBox();

    const currentCloudBoundingBox = currentCloud.cloud.geometry.boundingBox!;
    const { x: minX, y: minY, z: minZ } = currentCloudBoundingBox.min;
    const { x: maxX, y: maxY, z: maxZ } = currentCloudBoundingBox.max;
    const positionX = random(minX, maxX, true);
    const positionY = random(minY, maxY, true);
    const positionZ = random(minZ, maxZ, true);

    const shapeLocation: CuboidLocationState3D = {
      frame_number: frameNumber,
      points: [],
      visibility: this.isVisible ? 1 : 0,
      position: [positionX, positionY, positionZ],
      rotation: [0, 0, 0],
      scale: [2, 5, 1.8],
    };
    return shapeLocation;
  }

  private pickFrameNumbers() {
    if (this.useSingleFrame) {
      return [this.store.state().render.frameNumber];
    }

    const numFrames =
      this.store.state().sensorRendererState.sensorFrames.length;
    const numKeyframes = Math.ceil(
      (numFrames * this.percentageOfKeyLocationsToAdd) / 100,
    );

    const frameNumbers = times(numFrames, identity);
    while (frameNumbers.length > numKeyframes) {
      pullAt(frameNumbers, random(frameNumbers.length));
    }
    return frameNumbers;
  }
}
